/**
 * 社外依頼メールの雛形(hr:admin)。向き(発行 / 回収)× 言語(日本語 / English)。
 *
 * タブは `forceRender` で全部作る。**開いていないタブの入力も一緒に保存したい**ため。
 * プレビューは未保存の文面をそのまま描ける(保存してから試す、をしなくてよい)。
 * 「既定に戻す」はその 1 組だけを設定から消し、以後は既定に追従する。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Popconfirm,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { ExternalMailSettings, ExternalMailTexts } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  EXTERNAL_MAIL_BODY_MAX,
  EXTERNAL_MAIL_KINDS,
  EXTERNAL_MAIL_LANGS,
  EXTERNAL_MAIL_SUBJECT_MAX,
} from "./constants";
import { errorText, useHrPerms } from "./hooks";

const { Text, Paragraph } = Typography;

type FormValues = { external_mail: ExternalMailTexts };

function MailFields({ kind, lang }: { kind: string; lang: string }) {
  return (
    <>
      <Form.Item
        name={["external_mail", kind, lang, "subject"]}
        label="件名"
        rules={[{ max: EXTERNAL_MAIL_SUBJECT_MAX, message: `件名は ${EXTERNAL_MAIL_SUBJECT_MAX} 文字まで` }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={["external_mail", kind, lang, "body"]}
        label="本文"
        rules={[{ max: EXTERNAL_MAIL_BODY_MAX, message: `本文は ${EXTERNAL_MAIL_BODY_MAX} 文字まで` }]}
      >
        <Input.TextArea rows={12} />
      </Form.Item>
    </>
  );
}

export function ExternalMailPage() {
  const api = useApi();
  const { canAdmin } = useHrPerms();
  const [form] = Form.useForm<FormValues>();
  const [kind, setKind] = useState(EXTERNAL_MAIL_KINDS[0].value);
  const [lang, setLang] = useState(EXTERNAL_MAIL_LANGS[0].value);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);

  const query = useQuery({
    queryKey: ["hr", "external-mail"],
    queryFn: api.externalMail,
  });

  const loaded = query.data;
  useEffect(() => {
    if (loaded) form.setFieldsValue({ external_mail: loaded.external_mail });
  }, [loaded, form]);

  const apply = (out: ExternalMailSettings) => {
    form.setFieldsValue({ external_mail: out.external_mail });
    query.refetch().catch(() => undefined);
  };

  const save = useMutation({
    mutationFn: () =>
      api.saveExternalMail(form.getFieldsValue(true).external_mail as ExternalMailTexts),
    onSuccess: (out) => {
      void message.success("保存した");
      apply(out);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const tryPreview = useMutation({
    mutationFn: () => {
      const texts = form.getFieldsValue(true).external_mail as ExternalMailTexts;
      const one = texts?.[kind]?.[lang] ?? { subject: "", body: "" };
      return api.previewExternalMail({ kind, lang, subject: one.subject, body: one.body });
    },
    onSuccess: (out) => setPreview(out),
    onError: (e) => void message.error(errorText(e)),
  });

  const reset = useMutation({
    mutationFn: () => api.resetExternalMail(kind, lang),
    onSuccess: (out) => {
      void message.success("既定に戻した");
      setPreview(null);
      apply(out);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const isDefault = query.data?.is_default?.[kind]?.[lang] ?? true;

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }} gap={12} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          社外依頼メール
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
            保存
          </Button>
        )}
      </Flex>

      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="閲覧だけできる"
          description="雛形を直せるのは hr:admin だけ。"
        />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="雛形を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small" title="差し込めるもの" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {(query.data?.placeholders ?? []).map((p) => (
            <Text key={p.key} code style={{ marginRight: 6 }}>
              {"{" + p.key + "}"}
            </Text>
          ))}
          <br />
          {(query.data?.placeholders ?? []).map((p) => `{${p.key}} = ${p.label}`).join(" / ")}
        </Paragraph>
      </Card>

      <Form form={form} layout="vertical" disabled={!canAdmin}>
        <Tabs
          activeKey={kind}
          onChange={(k) => {
            setKind(k);
            setPreview(null);
          }}
          items={EXTERNAL_MAIL_KINDS.map((k) => ({
            key: k.value,
            label: k.label,
            forceRender: true,
            children: (
              <Tabs
                size="small"
                activeKey={lang}
                onChange={(l) => {
                  setLang(l);
                  setPreview(null);
                }}
                items={EXTERNAL_MAIL_LANGS.map((l) => ({
                  key: l.value,
                  label: l.label,
                  forceRender: true,
                  children: <MailFields kind={k.value} lang={l.value} />,
                }))}
              />
            ),
          }))}
        />
      </Form>

      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Space wrap align="center">
          {isDefault ? <Tag>既定のまま</Tag> : <Tag color="blue">直している</Tag>}
          <Button loading={tryPreview.isPending} onClick={() => tryPreview.mutate()}>
            プレビュー
          </Button>
          {canAdmin && (
            <Popconfirm
              title="この組を既定に戻す"
              description="いま開いている 向き × 言語 の 1 組だけを既定に戻す(保存していない入力は消える)"
              okText="戻す"
              cancelText="やめる"
              onConfirm={() => reset.mutate()}
            >
              <Button danger loading={reset.isPending}>
                既定に戻す
              </Button>
            </Popconfirm>
          )}
          <Text type="secondary">プレビューは入力中の文面をそのまま見本値で描く(保存しなくてよい)</Text>
        </Space>

        {preview && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="件名">{preview.subject}</Descriptions.Item>
            <Descriptions.Item label="本文">
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                {preview.body}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>
    </div>
  );
}
